import { updateLastSavedStep, getPhaseRecord, getDetailsForPhases as getDetails } from '../api/form-wizard.service';
import {
  updateLastSavedStep as externalUpdateLastSavedStep,
  getExternalRecord
} from '/src/features/external-forms/api/external-forms.service.js';
import { isWizardTypeMultiple } from '../utils';
import { modifyObjectFields } from '/src/features/object-records/api/object-service';

export const actions = {
  async onPhaseStepChange(
    ctx,
    {
      isExternal,
      wizardDetails,
      isUrlRedirection,
      params,
      recordUUID,
      event,
      currentPhaseDetails,
      isWorkflowTask,
      isUnfoldableTask,
      StepVisitation
    }
  ) {
    const router = useRouter();
    if (isExternal && router.currentRoute._value.params.recordUUID) {
      await ctx.dispatch('externalPhaseOrStepChange', {
        event,
        isExternal,
        params,
        wizardDetails,
        recordUuid: recordUUID,
        StepVisitation
      });
    } else if (isExternal && !router.currentRoute._value.params.recordUUID) {
      const { phase, step } = event;
      ctx.commit('setActivePhaseAndStep', { value: { phase, step } });
    } else {
      await ctx.dispatch('internalPhaseOrStepChange', {
        ...event,
        wizardDetails,
        isUrlRedirection,
        recordUUID,
        currentPhaseDetails,
        isWorkflowTask,
        isUnfoldableTask,
        StepVisitation
      });
    }
  },
  async internalPhaseOrStepChange(
    { commit, rootGetters, state },
    { wizardDetails, isUrlRedirection, phase, step, recordUUID, isWorkflowTask, isUnfoldableTask, StepVisitation }
  ) {
    // if phase not set, keep current phase

    if (!isUrlRedirection) {
      commit('setActivePhaseAndStep', { value: { phase, step } });
      const router = useRouter();
      const route = useRoute();
      if (isWorkflowTask && !isUnfoldableTask) {
        router.replace({
          query: {
            ...route.query,
            phase: state.phases[phase].Name,
            step: state.phases[phase].Steps[step] ? state.phases[phase].Steps[step].Name : undefined
          }
        });
      } else if (!isUnfoldableTask) {
        navigateTo({
          name: 'wizard-url-recordUUID-phase-step-mode',
          params: {
            url: wizardDetails.URL.split('/')[1],
            recordUUID: recordUUID || router.currentRoute._value.params.recordUUID,
            phase: state.phases[phase].Name,
            step: state.phases[phase].Steps[step] ? state.phases[phase].Steps[step].Name : undefined,
            mode: router.currentRoute._value.params.mode
          }
        });
      }
    }
    if (isUrlRedirection) {
      step = state.activeStep;
      phase = state.activePhase;
    }

    const stepData = {
      stepUUID: state.phases[phase].Steps[step] ? state.phases[phase].Steps[step].UUID : undefined,
      StepVisitation
    };
    updateLastSavedStep(
      recordUUID || this.$router.currentRoute.params.recordUUID,
      wizardDetails.UUID,
      rootGetters.userID,
      stepData
    );
  },
  async externalPhaseOrStepChange({ state, commit }, { wizardDetails, params, recordUuid, event, StepVisitation }) {
    /* committing active phase and state first so when last step is updated is not dependent on the event object */
    commit('setActivePhaseAndStep', { value: event });

    const stepData = {
      stepUUID: state.phases[state.activePhase]?.Steps[state.activeStep]
        ? state.phases[state.activePhase].Steps[state.activeStep].UUID
        : undefined,
      StepVisitation
    };

    externalUpdateLastSavedStep(params.tenantEntryUUID, recordUuid, wizardDetails.UUID, stepData);
  },

  async getRecord(
    ctx,
    {
      isExternal,
      recordUuid,
      objectUuid,
      params,
      wizardDetails,
      currentPhaseDetails,
      externalRecord,
      recordKey,
      taskUUID
    }
  ) {
    let record = {};
    const phaseDetails = currentPhaseDetails ? { ...currentPhaseDetails, taskUUID } : { taskUUID };
    if (isExternal) {
      if (recordUuid) {
        record = await getExternalRecord(objectUuid, recordUuid, params.tenantEntryUUID);
      }
      record = { ...externalRecord, ...record };
    } else {
      record = recordUuid ? await getPhaseRecord(wizardDetails, objectUuid, recordUuid, phaseDetails) : {};
    }
    ctx.commit('setRecordDetails', { recordUUID: recordKey ? recordKey : recordUuid, recordDetails: record });
  },

  async getDetailsForSingleObject(
    ctx,
    {
      objectUUID,
      recordUUID,
      objectFieldsMap,
      objectDetailsMap,
      recordKey,
      wizardDetails,
      currentPhaseDetails,
      isExternal,
      externalRecord,
      params,
      taskUUID
    }
  ) {
    await Promise.all([
      objectDetailsMap[objectUUID] || this.dispatch('template-caching-store/getObjectDetails', objectUUID),
      ctx.dispatch('getRecord', {
        isExternal: isExternal,
        recordUuid: recordUUID,
        objectUuid: objectUUID,
        params,
        wizardDetails,
        currentPhaseDetails,
        externalRecord,
        recordKey,
        taskUUID
      }),
      objectFieldsMap[objectUUID] || this.dispatch('template-caching-store/getObjectFields', objectUUID)
    ]);
  },

  async getObjectFieldAndRecordDetails(
    ctx,
    {
      recordUUID,
      wizardDetails,
      objectFieldsMap,
      objectDetailsMap,
      isExternal,
      params,
      currentPhaseDetails,
      externalRecord,
      taskUUID
    }
  ) {
    const isWizardMultiple = isWizardTypeMultiple(wizardDetails);
    if (isWizardMultiple) {
      await ctx.dispatch('getDetailsForMultipleObject', {
        recordUUID,
        wizardUUID: wizardDetails.UUID,
        taskUUID
      });
    } else {
      await ctx.dispatch('getDetailsForSingleObject', {
        objectUUID: wizardDetails.ObjectUUID,
        recordUUID,
        objectFieldsMap,
        objectDetailsMap,
        currentPhaseDetails,
        wizardDetails,
        isExternal,
        params,
        externalRecord,
        taskUUID
      });
    }
  },

  async getDetailsForMultipleObject(ctx, { recordUUID, wizardUUID, taskUUID }) {
    const result = (await getDetails(wizardUUID, recordUUID, taskUUID)).sParseJSON();
    if (!result.error) {
      Object.keys(result.fieldDetailsMap || []).forEach((UUID) => {
        let modifiedFields = result.fieldDetailsMap[UUID]
          ? modifyObjectFields(result.fieldDetailsMap[UUID])
          : result.fieldDetailsMap[UUID];
        result.fieldDetailsMap[UUID] = modifiedFields;
      });
      this.commit('template-caching-store/setMultipleObjectDetails', result.objectDetailsMap);
      this.commit('template-caching-store/setFieldDetailsForMultipleObjects', result.fieldDetailsMap);
      const recordKeys = Object.keys(result?.recordDetails || []);
      recordKeys.forEach((key) => {
        const recordData = result.recordDetails[key];
        ctx.commit('setRecordDetails', { recordUUID: key, recordDetails: recordData });
      });
    }
  }
};
